import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { RefreshCw, AlertTriangle, ArrowRight } from 'lucide-react';
import { Button } from '../ui/Button';
import { SkeletonDashboard } from '../ui/Skeleton';
import { getMorningBrief } from '../../services/brief';
import type { MorningBrief as MorningBriefData } from '../../data/types';
import { BriefHero } from './BriefHero';
import { BriefNarrative } from './BriefNarrative';
import { PriorityQueue } from './PriorityQueue';
import { TenantPulse } from './TenantPulse';
import { CveRelevance } from './CveRelevance';
import { AgentActivityLog } from './AgentActivityLog';
import { appEnv } from '../../lib/env';

export function MorningBrief() {
  const [brief, setBrief] = useState<MorningBriefData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchBrief = async (refresh = false) => {
    if (refresh) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }
    setError(null);

    const result = await getMorningBrief();
    if (result.success) {
      setBrief(result.data);
    } else {
      setError(result.error || 'Brief unavailable');
    }
    setIsLoading(false);
    setIsRefreshing(false);
  };

  useEffect(() => {
    fetchBrief();
  }, []);

  if (isLoading) return <SkeletonDashboard />;

  if (error || !brief) {
    return (
      <div className="max-w-[1600px] mx-auto p-6">
        <div className="bg-accent-red/10 border border-accent-red/30 rounded-lg p-6 flex items-start gap-4">
          <AlertTriangle className="w-6 h-6 text-accent-red flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <h3 className="font-sans font-medium text-text-primary mb-1">Morning brief unavailable</h3>
            <p className="text-sm text-text-secondary">{error ?? 'Unknown error'}</p>
          </div>
          <Button onClick={() => fetchBrief()} variant="primary" size="sm">
            <RefreshCw className="w-4 h-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const generatedDate = new Date(brief.generatedAt);
  const dateStr = generatedDate.toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
  const timeStr = generatedDate.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZoneName: 'short',
  });

  const sinceLast = brief.structured.sinceLast;
  const clusters = countClusters(brief.structured.priorityIncidents.map(p => p.cluster));

  return (
    <div className="-m-6 animate-fade-in">
      {/* Sub-bar */}
      <div className="flex flex-wrap items-end justify-between gap-6 px-6 lg:px-8 py-4 bg-bg-secondary border-b border-border-default">
        <div>
          <div className="text-[10px] tracking-[0.22em] uppercase text-text-secondary font-bold font-sans">
            Morning brief · {dateStr} · {timeStr}
          </div>
          <h1 className="font-sans text-[24px] font-medium tracking-[-0.015em] my-1.5 text-text-primary">
            Good morning, {brief.analyst.firstName}.
          </h1>
          <div className="text-[12.5px] text-text-secondary font-sans">
            <strong>{sinceLast.newIncidents}</strong> new incidents since last sign-out,{' '}
            <strong>{sinceLast.escalated}</strong> escalated, <strong>{sinceLast.closedByOthers}</strong> closed
            by others.
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => fetchBrief(true)} disabled={isRefreshing}>
            <RefreshCw className={isRefreshing ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
            Re-run brief
          </Button>
          <Link to="/incidents">
            <Button variant="primary" size="sm" onClick={() => navigate('/incidents')}>
              Open queue
              <ArrowRight className="w-4 h-4" />
            </Button>
          </Link>
        </div>
      </div>

      {/* Canvas */}
      <div className="px-6 lg:px-8 py-6 pb-16">
        <BriefHero queue={brief.structured.queue} sinceLast={sinceLast} clusterCount={clusters} />

        <BriefNarrative
          narrative={brief.narrative}
          generatedAt={brief.generatedAt}
          generatedBy={brief.agent.generatedBy}
          modelLatencyMs={brief.agent.modelLatencyMs}
          scannedCount={brief.structured.queue.total}
        />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3.5">
          <PriorityQueue
            incidents={brief.structured.priorityIncidents}
            currentUserName={brief.analyst.displayName}
          />
          <TenantPulse tenants={brief.structured.tenants} />
          {!appEnv.isProd && (
            <CveRelevance cves={brief.structured.cves} source={brief.structured.cveSource} />
          )}
          <AgentActivityLog runs={brief.structured.agentRuns} wide={appEnv.isProd} />
        </div>
      </div>
    </div>
  );
}

function countClusters(clusters: Array<string | null>): number {
  const set = new Set<string>();
  for (const c of clusters) if (c) set.add(c);
  return set.size;
}
